import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { datadogRum } from '@datadog/browser-rum'
import { appWithTranslation } from 'next-i18next'
import TagManager from 'react-gtm-module'
import { AwsRum } from 'aws-rum-web'
import Head from 'next/head'
import { PAGE_PATHS } from '@/services/paths'

import { GlobalStyle } from '@/styles/global'
import { WindowSizeProvider } from '@/contexts/WindowSizeContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { OnboardingProvider } from '@/contexts/OnboardingContext'
import { ModalProvider } from '@/contexts/ModalContext'
import { HistoryProvider } from '@/contexts/HistoryContext'
import { StatsigProvider } from '@/contexts/StatsigContext'

import Tracked from '@/helpers/PageTypes/Tracked'
import useOneTrustVisibility from '@/hofs/useOneTrustVisibility'

import ErrorBoundary from '@/components/ErrorBoundary'
import Modal from '@/components/Modal'
import ZendeskWrapper from '@/components/Zendesk'

const BioAtlas = ({ Component, pageProps }) => {
  const didMount = useRef(false)
  const router = useRouter()
  useOneTrustVisibility()

  // TabNav dynamic loading based on route
  useEffect(() => {
    const excludedPaths = [
      PAGE_PATHS.viewer,
      PAGE_PATHS.embedded,
      PAGE_PATHS.dicom,
      PAGE_PATHS.viewer3D
    ]

    const shouldLoadTabNav = !excludedPaths.some(path =>
      router.pathname.toLowerCase().startsWith(path.toLowerCase())
    )

    // Remove existing TabNav elements
    const removeTabNav = () => {
      const existingScript = document.querySelector('script[src*="tabnav.com"]')
      if (existingScript) {
        existingScript.remove()
      }
      const tabNavWidget = document.querySelector(
        '[class*="tabnav"], [id*="tabnav"]'
      )
      if (tabNavWidget) {
        tabNavWidget.remove()
      }
    }

    if (shouldLoadTabNav) {
      // Load TabNav
      const browserLang = navigator.language || navigator.language || 'en-US'
      const langParts = browserLang.split('-')
      const langCode = langParts[0].toLowerCase()
      const regionCode = langParts[1] ? langParts[1].toLowerCase() : ''

      let tabNavLang = 'us-en'

      if (langCode === 'pt') {
        if (regionCode === 'br') {
          tabNavLang = 'br'
        } else {
          tabNavLang = 'pt'
        }
      } else if (langCode === 'es') {
        tabNavLang = 'es'
      } else if (langCode === 'en') {
        tabNavLang = 'us-en'
      }

      const configStr = JSON.stringify({
        language: tabNavLang,
        color: '#405ec3',
        buttonColor: '#405ec3',
        buttonSize: 'small',
        widgetSize: 'small',
        widgetLocation: 'left',
        buttonLocation: 'bottom'
      })

      const script = document.createElement('script')
      script.src =
        'https://widget.tabnav.com/limited-widget.min.js.gz?req=kYYv326OVadxjj9XxabWGujOwb1Wo8wxIufAJDJLiAZPMA'
      script.setAttribute('tnv-data-config', configStr)
      script.defer = true
      script.id = 'tabnav-script'

      // Remove existing before adding new
      removeTabNav()
      document.head.appendChild(script)
    } else {
      // Remove TabNav if on excluded path
      removeTabNav()
    }
  }, [router.pathname])

  useEffect(() => {
    if (didMount.current) return

    // -------- GOOGLE TAG MANAGER --------- //
    TagManager.initialize({
      gtmId: process.env.GTM_ID,
      auth: process.env.GTM_AUTH,
      preview: process.env.GTM_PREVIEW
    })

    // -------- MAZE --------- //
    const maze = (m, a, z, e) => {
      let t
      try {
        t = m.sessionStorage.getItem('maze-us')
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err: unknown) {}

      if (!t) {
        t = new Date().getTime()
        try {
          m.sessionStorage.setItem('maze-us', t)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (err: unknown) {}
      }

      const s = a.createElement('script')
      s.src = z + '?t=' + t + '&apiKey=' + e
      s.async = true
      a.getElementsByTagName('head')[0].appendChild(s)
      m.mazeUniversalSnippetApiKey = e
    }

    maze(
      window,
      document,
      'https://snippet.maze.co/maze-universal-loader.js',
      '70a32d99-d483-484b-ba98-f1ef745c49d7'
    )

    // -------- AWS RUM --------- //
    try {
      const config = {
        sessionSampleRate: 1,
        identityPoolId: process.env.RUM_APP_POOL,
        endpoint: process.env.RUM_APP_ENDPOINT,
        telemetries: ['performance', 'errors', 'http'],
        allowCookies: true,
        enableXRay: true
      }

      const APPLICATION_ID = process.env.RUM_APP_ID
      const APPLICATION_VERSION = '1.0.0'
      const APPLICATION_REGION = process.env.RUM_APP_REGION

      if (process.env.NODE_ENV !== 'development') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const awsRum = new AwsRum(
          APPLICATION_ID,
          APPLICATION_VERSION,
          APPLICATION_REGION,
          config
        )
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      // Ignore errors thrown during CloudWatch RUM web client initialization
    }
    // ---------- DATADOG RUM --------- //
    try {
      datadogRum.init({
        applicationId: process.env.DATADOG_APPLICATION_ID,
        clientToken: process.env.DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'bioatlas',
        env: process.env.DATADOG_ENV,
        // Specify a version number to identify the deployed version of your application in Datadog
        version: process.env.NEXT_PUBLIC_DATADOG_APP_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: +process.env.DATADOG_SAMPLE_RATE,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        storeContextsAcrossPages: true
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error: unknown) {
      // Ignore errors thrown during DATADOG RUM web client initialization
    }

    didMount.current = true
  }, [])

  return (
    <HistoryProvider>
      <Tracked>
        <WindowSizeProvider>
          <AuthProvider tokenData={pageProps?.tokenData}>
            <StatsigProvider>
              <OnboardingProvider>
                <ModalProvider>
                  <Head>
                    <meta
                      name="viewport"
                      content="width=device-width, initial-scale=1, minimum-scale=1, viewport-fit=cover"
                    />
                  </Head>
                  <ErrorBoundary>
                    <ZendeskWrapper>
                      <Component {...pageProps} />
                      <Modal />
                    </ZendeskWrapper>
                  </ErrorBoundary>
                  <GlobalStyle />
                </ModalProvider>
              </OnboardingProvider>
            </StatsigProvider>
          </AuthProvider>
        </WindowSizeProvider>
      </Tracked>
    </HistoryProvider>
  )
}

export default appWithTranslation(BioAtlas)
